/**
 * Debrief / completion page for the Information Sharing study.
 * Simple thank-you + Prolific completion redirect.
 * Does NOT include FTT trait measures or manipulation checks.
 *
 * SAVE-CONFIRMED GATE: the participant cannot reach the Prolific completion link
 * until their data is confirmed persisted (or, if the network is down, safely
 * queued). This is the last line of defense against silent data loss — every
 * completed participant is money, so we never let one finish with unsaved data.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { savePhaseData, completeExperiment } from '../../services/api';

// The Prolific completion URL/code are NO LONGER baked into the client build.
// The backend releases them in the /complete response, only AFTER it validates
// the data — so the code can't be scraped from the page source without finishing
// the study. We capture whatever the server returns and redirect to that.
const validUrl = (u?: string) => !!u && !u.includes('XXXXXXXX');

type SaveState = 'saving' | 'confirmed' | 'failed';

const InfoSharingDebrief: React.FC = () => {
  const [saveState, setSaveState] = useState<SaveState>('saving');
  const [attempt, setAttempt] = useState(0);
  const [completionUrl, setCompletionUrl] = useState<string>('');
  const [completionCode, setCompletionCode] = useState<string>('');

  // Flush the Study 2 backup to the durable phase endpoint. savePhaseData also
  // replays its own localStorage retry queue, so this re-sends anything that
  // failed earlier. Resolves only when the server confirms the write.
  const flush = useCallback(async () => {
    setSaveState('saving');
    try {
      const participantId = localStorage.getItem('participantId');
      if (!participantId) {
        // A missing id means initialization failed — this session has NO saved
        // data. Do NOT wave them through to the Prolific completion link (they
        // would be paid for data we never captured). Block and surface the error.
        console.error('InfoSharing debrief: no participantId — session was never initialized.');
        setSaveState('failed');
        return;
      }

      const backupRaw = localStorage.getItem(`infoSharingBackup_${participantId}`);
      const payload = backupRaw
        ? JSON.parse(backupRaw)
        : { endTime: Date.now(), responses: [] };

      await savePhaseData(participantId, 'info-sharing-task', payload);

      // Mark the participant completed (completed=true, endTime, currentPhase) so
      // finishers are distinguishable from drop-offs in analysis. This is SECONDARY
      // to the data save above: the article data is already persisted, so a failure
      // to set the completion flag must NOT strand a paid participant at the Prolific
      // link — log it and proceed to 'confirmed'.
      try {
        const res = await completeExperiment(participantId, {
          completedAt: payload.endTime || Date.now(),
          articlesCompleted: Array.isArray(payload.responses) ? payload.responses.length : undefined,
          source: 'info-sharing',
        });
        // Capture the server-released completion URL/code (only present on a
        // validated completion). This is the redirect target.
        if (res?.completionUrl) setCompletionUrl(res.completionUrl);
        if (res?.completionCode) setCompletionCode(res.completionCode);
      } catch (e) {
        console.error('InfoSharing: completion-flag update failed (data already saved):', e);
      }

      // Confirmed persisted — safe to clear the local backup, and mark the task
      // complete so a reload of '/' cannot re-run it (replay guard in App.tsx).
      try {
        localStorage.removeItem(`infoSharingBackup_${participantId}`);
        localStorage.setItem('infoSharingComplete', 'true');
      } catch {}
      setSaveState('confirmed');
    } catch (e) {
      console.error('InfoSharing debrief flush failed:', e);
      setSaveState('failed');
    }
  }, []);

  useEffect(() => {
    flush();
  }, [flush, attempt]);

  // Auto-redirect ONLY once the save is confirmed AND the server returned a real
  // completion URL. Never redirect to a placeholder/empty URL (would strand them).
  useEffect(() => {
    if (saveState !== 'confirmed' || !validUrl(completionUrl)) return;
    const timer = setTimeout(() => {
      window.location.href = completionUrl;
    }, 6000);
    return () => clearTimeout(timer);
  }, [saveState, completionUrl]);

  return (
    <div style={{ maxWidth: '600px', margin: '80px auto', padding: '2rem', textAlign: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1rem', color: '#1a202c' }}>
        Thank You for Participating
      </h1>

      <div style={{
        padding: '1.5rem', backgroundColor: '#f8f9fa', borderRadius: '8px',
        border: '1px solid #e9ecef', marginBottom: '2rem', textAlign: 'left',
      }}>
        <p style={{ fontSize: '1rem', lineHeight: 1.7, color: '#2d3748', textAlign: 'center', fontWeight: 600, margin: 0 }}>
          Thank you for participating.
        </p>
      </div>

      {/* Save state */}
      {saveState === 'saving' && (
        <div style={{ marginBottom: '1.5rem' }}>
          <p style={{ fontSize: '0.95rem', color: '#4a5568' }}>
            Saving your responses… please don't close this window.
          </p>
        </div>
      )}

      {saveState === 'failed' && (
        <div style={{
          padding: '1rem', backgroundColor: '#fff5f5', border: '1px solid #feb2b2',
          borderRadius: '8px', marginBottom: '1.5rem',
        }}>
          <p style={{ fontSize: '0.9rem', color: '#c53030', marginBottom: '0.75rem' }}>
            We couldn't confirm your responses were saved. Your data is safe on this
            device — please click below to try again before completing the study.
          </p>
          <button
            onClick={() => setAttempt(a => a + 1)}
            style={{
              padding: '10px 28px', fontSize: '14px', fontWeight: 600,
              backgroundColor: '#e53e3e', color: 'white', border: 'none',
              borderRadius: '8px', cursor: 'pointer',
            }}
          >
            Retry Saving
          </button>
        </div>
      )}

      {saveState === 'confirmed' && (
        <>
          <p style={{ fontSize: '1rem', lineHeight: 1.8, color: '#2f855a', marginBottom: '1rem', fontWeight: 600 }}>
            ✓ Your responses have been recorded.
          </p>
          {validUrl(completionUrl) ? (
            <p style={{ fontSize: '0.85rem', color: '#999', marginBottom: '1.5rem' }}>
              You will be redirected to Prolific in a few seconds to confirm your completion.
              {completionCode && <> If you are not redirected, your completion code is <b>{completionCode}</b>.</>}
            </p>
          ) : (
            <p style={{ fontSize: '0.9rem', color: '#c05621', marginBottom: '1.5rem' }}>
              Your data is saved. The completion link is being finalized — please
              message the research team on Prolific to confirm your completion.
            </p>
          )}
        </>
      )}

      {/* Real completion link, only once data is confirmed and the server has
          released a valid URL (never baked into the JS, never a placeholder). */}
      {validUrl(completionUrl) && saveState === 'confirmed' && (
        <a
          href={completionUrl}
          style={{
            display: 'inline-block',
            padding: '14px 40px', fontSize: '16px', fontWeight: 600,
            backgroundColor: '#28a745', color: 'white', border: 'none',
            borderRadius: '8px', textDecoration: 'none',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)', cursor: 'pointer',
          }}
        >
          Complete Study on Prolific
        </a>
      )}

      <p style={{ fontSize: '0.8rem', color: '#aaa', marginTop: '2rem' }}>
        If you have questions about this study, please contact the research team through Prolific messaging.
      </p>
    </div>
  );
};

export default InfoSharingDebrief;
